<template>
  <b-container fluid>
    <b-container v-show="isLoading" class= "loader-section" ref="loader-section">
      <frontoffice-loader/>
    </b-container>

    <b-row v-show="!isLoading" class="online-results" ref="results-section">
      <b-col cols="12" xl="6" class="search">
        <div class="positionFixed">
          <div class="toggle-frame">
            <div class="toggle-link">
              <a @click="isShownForm = !isShownForm" class="toggle-link-content">
                <span class="search-ico"><i class="fas fa-search"></i></span>

                <span>{{ searchTitle }}</span>

                <span class="chevron-ico text-right">
                  <i :class="isShownForm ? 'up' : 'down'" class="fas fa-chevron-down"></i>
                </span>
              </a>
            </div>

            <div v-show="isShownForm">
              <!-- Le container bootstrap est dans le composant online booking form -->
              <online-booking-form :submit-callbacks="onFormSubmit"
                                   :submit-button-text="submitButtonText"
              />
            </div>
          </div>

          <div class="map-link" v-if="isViewPortMobile && !isViewPortMobileLarge">
            <a @click="toggleMap">
              <span>
                <span><i class="fas fa-map-marked-alt"></i></span>
                <span>{{ mapMessage }}</span>
              </span>
            </a>
          </div>

          <b-col cols="12" lg="12" v-show="isShownMap" class="map"
                 :class="isShownMap ? 'add-margin' : 'frame'"
          >
            <l-map ref="hotelMap" :zoom="13" :center="leaflet.center" :options="leaflet.options" class="resultMap">
              <l-tile-layer :url="leaflet.url" :attribution="leaflet.attribution" />
            </l-map>
          </b-col>
        </div>
      </b-col>

      <filters v-if="!isLoading" ref="filters" :min-price="Math.floor(origPrices.min)" :max-price="Math.ceil(origPrices.max)"
               :orig-min-price="Math.floor(origPrices.min)" :orig-max-price="Math.ceil(origPrices.max)" :initFilters="filter.selected"
               @update="updateFilters"
               @submit="submitFilters"
               @reset-all="resetFilters(true)"
      />

      <b-col cols="12" xl="6" class="results">
        <div v-if="offers !== null">
          <b-row class="filters-btn-container">
            <div class="col-8 col-md-9">
              <button class="btn-toggle-pan btn white-blue" @click="slideFiltersPan">
                <i class="fas fa-filter"></i>
                <span>{{ $t('_filter.filtering') }}</span>
              </button>

              <b-dropdown v-if="result !== 0" id="dropdown-1" class="sort-dropdown"
                          ref="sortDropDown" :text="sortTitle" :disabled="isReloading"
              >
                <sort :trans-key="sort.transKey" :options="sort.options"
                      @change="updateSort"
                />
              </b-dropdown>

              <b-dropdown id="show-dropdown" ref="showDropdown" text="Mode d'affichage" class="show-dropdown">
                <sort :trans-key="sortShow.transKey" :options="sortShow.options"
                      @change="updateShow"
                />
              </b-dropdown>
            </div>

            <div class="col-4 col-md-3 text-right">
              <b-button class="btn white-blue" @click="resetFilters"
                        v-if="filterBadges.length"
              >
                <div>
                  {{ $t('_delete') }}
                </div>
              </b-button>
            </div>
          </b-row>

          <b-row>
            <div class="col-12 filters-badges-container">
              <span class="badges">
                <b-badge v-for="badge in filterBadges"
                         :title="badge.length > 15 ? badge : ''"
                         v-b-tooltip="{ container:'.online-results', placement: 'bottom'}"
                >
                  {{ badge |truncate(15) }}
                </b-badge>
              </span>
            </div>
          </b-row>

          <b-row v-if="!isReloading && result">
            <div class="col-12">
              <p class="results-number text-right">
                {{ result }} {{ $tc('_result',result) }}
              </p>
            </div>
          </b-row>

          <b-container v-if="isReloading" class= "loader-section">
            <frontoffice-loader/>
          </b-container>

          <b-row v-else-if="result !== 0" v-for="(offer, key) in offers" :key="key" class="ml-1">
            <div v-if="Object.keys(offers).length > 1" class="col-12 listeHotels text-center">
              <h2>{{ $t(offer.title) }}</h2>
            </div>

            <div class="col-12">
              <room-offer-card v-for="offer in offer.hotels"
                               :key="offer.hotelKey"
                               :ref="offer.hotelKey" :offer="offer"
                               :selected="offer.selected" @click="updateSelectedCard"
              />
            </div>
            <hr v-if="Object.keys(offers).length > 1" />
          </b-row>
        </div>

        <b-container v-else-if="isReloading" class="loader-section">
          <frontoffice-loader/>
        </b-container>

        <div v-else-if="searchMade">
          {{ $t('booking.no_offer_this_date') }}
        </div>
      </b-col>
    </b-row> <!-- results -->
  </b-container>
</template>

<script>
import frontofficeLoader from '@/components/frontoffice/loader'
import OnlineBookingForm from '@/components/frontoffice/OnlineBooking/home/OnlineBookingForm'
import RoomOfferCard from '@/components/frontoffice/OnlineBooking/RoomOfferCard'
import filters from '@/components/frontoffice/OnlineBooking/home/filters/boardCard'
import sort from '@/components/frontoffice/OnlineBooking/home/sort'

import FormSubmitter from '@/api/formSubmitter'

import viewPortMixin from '@/conf/viewPort'
import filtersMixin from '@/components/frontoffice/OnlineBooking/home/mixins/filters'
import sortMixin from '@/components/frontoffice/OnlineBooking/home/mixins/sort'
import mapMixin from '@/components/frontoffice/OnlineBooking/home/mixins/map'
import { formatDateForOnlineRequest } from '@/utils/date'

export default {
  name: 'onlineBookingHotelAvailabilities',

  components: {
    frontofficeLoader,
    OnlineBookingForm,
    RoomOfferCard,
    filters,
    sort
  },

  mixins: [viewPortMixin, sortMixin, filtersMixin, mapMixin],

  props: {
    duplicate: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      isLoading: false,
      offlineAvailable: true,
      serverError: false,
      isReloading: false,
      searchData: this.$store.state.research,
      defaultOffers: {},
      offers: {},
      isShownForm: false,
      minPrice: 0,
      maxPrice: 1000,
      searchMade: false,
      origPrices: {
        min: 0,
        max: 0
      },
      result: 0,
      sortShow: {
        transKey: 'online.sortShow.',
        options: [
          'negotiated',
          'simple'
        ],
        selected: null
      }
    }
  },

  created () {
    this.offlineAvailable = this.$store.getters['security/company'].offline
  },

  mounted () {
    // On récupère les filtres depuis le searchData, lui même récupéré depuis le store
    this.filter.selected = this.searchData.filters
    if (this.searchData.origPrices) { this.origPrices = this.searchData.origPrices }
    this.mapObject = this.$refs.hotelMap.mapObject
    if (this.duplicate) {
      this.isShownForm = true
      this.isLoading = false
    } else {
      this.searchMade = true
      this.search(true)
    }
  },

  methods: {
    onFormSubmit (e, data) {
      e.preventDefault()

      this.searchData.bedrooms = data.bedrooms
      this.searchData.dates.from = formatDateForOnlineRequest(data.dates.from)
      this.searchData.dates.to = formatDateForOnlineRequest(data.dates.to)
      this.searchData.destination = data.destination
      this.searchData.favorite = data.favorite

      this.isReloading = true
      // Empty map
      this.updateMap([])
      this.hydrateStore()
      this.search(!this.searchMade, true)
      this.isShownForm = false
    },

    search (init = false, reinitFilters = false) {
      if (!reinitFilters) this.isReloading = true
      let formSubmitter = new FormSubmitter('online_search')
      let filters = null
      if (!reinitFilters) filters = this.filter.selected
      let sort = init ? null : this.sort.selected
      let show = init ? null : this.sortShow.selected
      let disabled = false

      if (filters && filters.services && filters.services.some(f => { return f.value === 'disabled' })) { disabled = true }

      if (!filters) { filters = null }

      this.searchData.services.disabled = disabled

      // On pousse les filtres actuels dans le searchData, qui lui même est intégralement poussé dans le store
      this.searchData.filters = filters

      this.hydrateStore()

      this.result = 0

      formSubmitter.submitRequest(
        this.params,
        filters || [],
        sort,
        show
      )
        .then(response => {
          this.offers = {}
          this.searchMade = true
          Object.keys(response.data.offers).forEach(key => {
            if (response.data.offers[key].hotels.length !== 0) {
              this.defaultOffers[key] = response.data.offers[key] || []
              this.offers[key] = response.data.offers[key] || []
            }
          })

          if (response.data.minPrice) { this.minPrice = response.data.minPrice }
          if (response.data.maxPrice) { this.maxPrice = response.data.maxPrice }

          if (reinitFilters || this.origPrices.min == 0 || this.origPrices.max == 0) {
            if (response.data.minPrice) {
              this.origPrices.min = response.data.minPrice
            }
            if (response.data.maxPrice) {
              this.origPrices.max = response.data.maxPrice
            }
            this.searchData.origPrices = this.origPrices
            this.hydrateStore()
          }
        })
        .then(() => {
          // resetFilters sans relancer de recherche
          if (reinitFilters) { this.resetFilters(false, false) }

          this.updatePrices()
          // On update les filtres de badge après avoir récupérer min & max prices car on n'affiche pas les badges de prix si ils sont égaux aux bornes
          this.updateFilterBadges()
          // On boucle sur les clés de nos offres pour récupérer la liste des hôtels
          Object.keys(this.offers).forEach(key => {
            this.result += this.offers[key].hotels.length
            this.parseOffers(this.offers[key].hotels)
          })
        })
        .then(() => {
          this.isLoading = false
          this.isReloading = false
        })
        .catch((error) => {
          if (this.isLoading === true) this.serverError = true
          this.isLoading = false
        })
        .then(() => {
          this.updateMap(this.offers)
        })
    },

    updatePrices () {
      this.$refs.filters.update({
        name: 'price',
        value: {
          min: Math.floor(this.minPrice),
          max: Math.ceil(this.maxPrice)
        }
      })
    },

    updateSelectedCard (selectedOffer) {
      for (let i in this.$refs) {
        if (this.$refs.hasOwnProperty(i)) {
          if (this.$refs[i].length) {
            this.$refs[i][0].$data.isSelected = selectedOffer.hotelKey === i
          }
        }
      }

      this.updateOpenMarker(selectedOffer)
    },

    updateShow (selected) {
      this.sortShow.selected = selected
      this.$refs.showDropdown.visible = false
      this.search()
    },

    slideFiltersPan () {
      this.$refs.filters.slidePan()
    },

    hydrateStore () {
      this.$store.commit('research/all', this.searchData)
    },
    /*
    toggleMap: function(event) {
      this.mapObject.invalidateSize()
      this.isShownMap = !this.isShownMap
    }
*/

  },

  computed: {
    params () {
      return {
        destination: this.searchData.destination,
        favorite: this.searchData.favorite,
        startDate: this.searchData.dates.from,
        endDate: this.searchData.dates.to,
        singleRooms: this.searchData.bedrooms.single.amount,
        doubleRooms: this.searchData.bedrooms.double.amount
      }
    },

    searchTitle () {
      let trans = 'online.hotelAvailabilities.search.'

      if (!this.searchMade) trans += 'duplicate.'

      let res = this.$t(trans + 'title')
      if (this.offlineAvailable) { res += this.$t(trans + 'title_or_offline') }

      return res
    },

    submitButtonText () {
      return this.$t(
        this.searchMade ? '_modify' : 'online.hotelAvailabilities.search.duplicate.button'
      )
    }
  },

  watch: {
    offers (offers) {
      for (let i in offers) {
        if (offers.hasOwnProperty(i)) {
          let offer = offers[i]

          if (this.$refs[offer.hotelKey] && !offer.selected) {
            if (this.$refs[offer.hotelKey][0]) {
              this.$refs[offer.hotelKey][0].$data.isSelected = false
            }
          }
        }
      }
    },

    isViewPortMinMobileLarge (val) {
      this.isShownMap = val
    }
  }
}
</script>

<style lang="scss">
  @import '@/assets/sass/common/color.scss';

  .resultMap {
    height: 400px;
  }

  .leaflet-pane {
    &.leaflet-popup-pane {
      .leaflet-popup-content-wrapper {
        color: $orange;
        font-weight: bold;
      }

      .leaflet-popup-close-button {
        color: #1A2050; // $blue
      }
    }
  }

  .front-layout .online-results .show-dropdown .btn.dropdown-toggle::before {
    padding-right: 5px;
    font-family: 'Font Awesome 5 Free';
    content: '\F108';
    font-weight: 900;
    color: #1A2050;
  }
</style>
